import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParticipants } from '../hooks/useParticipants';
import { getHandlerRequestsByBreederId } from '../api/actions';
import WelcomeBanner from '../components/WelcomeBanner';
import BackToDashboardButton from '../components/BackToDashboardButton';
import '../components/ParticipantCard.css';

const BREEDER_BIRD_FIELDS = [
  'totalBirdsForRace',
  'totalBirdForRace',
  'totalBirdsForRaces',
  'totalBirdsRace',
  'totalBirds'
];

const ALLOWED_BIRD_STATUSES = new Set(['active', 'lost', 'died', 'injured']);
const MISSING_BIRD_INFO_LABEL = 'Bird info missing';
const BIRD_INFO_HELP_TEXT = 'Login to your account, go to View Handlers to Add/Edit bird info.';

const toNumber = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === 'string') {
    const parsed = Number(value.trim());
    return Number.isFinite(parsed) ? parsed : 0;
  }

  return 0;
};

const firstDefined = (...values) => values.find(value => value !== undefined && value !== null);

const normalizeId = (value) => String(value || '')
  .trim()
  .replace(/[{}]/g, '')
  .toLowerCase();

const toDisplayCase = (value) => {
  const input = String(value || '').trim();
  if (!input) return '';

  return input
    .split(/\s+/)
    .map((token) => {
      if (!token) return token;
      if (token.includes('.')) return token.toUpperCase();
      if (token.toUpperCase() === token && token.length <= 3) return token;
      return token.charAt(0).toUpperCase() + token.slice(1).toLowerCase();
    })
    .join(' ');
};

const hasRole = (participant, roleToCheck) => {
  if (!participant || !roleToCheck) return false;

  if (Array.isArray(participant.role)) {
    return participant.role.some(role => String(role).toLowerCase() === roleToCheck.toLowerCase());
  }

  return String(participant.role || '').toLowerCase() === roleToCheck.toLowerCase();
};

const getBreederBirdSlotCount = (breeder) => {
  const breederInfo = breeder?.breederInfo || breeder?.BreederInfo || breeder?.breederinfo;
  if (!breederInfo || typeof breederInfo !== 'object') return 0;

  for (const key of BREEDER_BIRD_FIELDS) {
    if (key in breederInfo) {
      return Math.max(0, toNumber(breederInfo[key]));
    }
  }

  return 0;
};

const normalizeBirdStatus = (status) => {
  const normalized = String(status || '').toLowerCase();
  return ALLOWED_BIRD_STATUSES.has(normalized) ? normalized : 'active';
};

const getRequiredRotationBirds = (totalBirds) => {
  const total = toNumber(totalBirds);

  if (total >= 30) return 3;
  if (total >= 12) return 2;
  if (total >= 5) return 1;
  return 0;
};

const isMissingBirdInfoText = (value) => {
  if (typeof value !== 'string') return true;
  const normalized = value.trim().toLowerCase();
  if (!normalized) return true;

  return normalized === 'to be added' ||
    normalized === '[to be added]' ||
    normalized === 'bird to be added' ||
    normalized === 'bird info missing';
};

const isMeaningfulBandNumber = (value) => {
  return !isMissingBirdInfoText(value);
};

const getBirdLabel = (bird) => {
  const bandNumber = bird?.bandNumber;
  if (isMeaningfulBandNumber(bandNumber)) {
    return bandNumber.trim();
  }

  return MISSING_BIRD_INFO_LABEL;
};

const isRotationRequest = (request) => {
  const handlerId = String(firstDefined(request?.handlerId, request?.HandlerId, request?.handlerID) || '').toLowerCase();
  return (
    handlerId === 'admin-rotation' ||
    handlerId === 'admin-rotation-mandatory' ||
    request?.isAdminRotation === true ||
    request?.IsMandatoryRotation === true ||
    request?.isMandatoryRotation === true
  );
};

const normalizeRequest = (request) => {
  const id = firstDefined(request?.id, request?.requestId, request?.RequestId);
  const breederId = firstDefined(request?.breederId, request?.BreederId, request?.breederID);
  const handlerId = firstDefined(request?.handlerId, request?.HandlerId, request?.handlerID);
  const handlerName = firstDefined(request?.handlerName, request?.HandlerName, request?.assignedHandlerName, request?.AssignedHandlerName);
  const numberOfBirds = firstDefined(
    request?.numberOfBirds,
    request?.NumberOfBirds,
    request?.birdsRequested,
    request?.BirdsRequested
  );
  const status = String(firstDefined(request?.status, request?.Status) || '').toLowerCase();
  const requestDate = firstDefined(request?.requestDate, request?.RequestDate);

  return {
    ...request,
    id,
    breederId,
    handlerId,
    handlerName,
    numberOfBirds,
    status,
    requestDate,
    birdsRequested: firstDefined(request?.birdsRequested, request?.BirdsRequested, numberOfBirds)
  };
};

const getRequestBirds = (request) => {
  if (Array.isArray(request?.birds)) return request.birds;
  if (Array.isArray(request?.Birds)) return request.Birds;
  if (Array.isArray(request?.birdDetails)) return request.birdDetails;
  if (Array.isArray(request?.BirdDetails)) return request.BirdDetails;
  if (Array.isArray(request?.assignedBirds)) return request.assignedBirds;
  if (Array.isArray(request?.AssignedBirds)) return request.AssignedBirds;
  return [];
};

const getRequestsFromSource = (source) => {
  if (Array.isArray(source)) return source;
  return source && typeof source === 'object' ? [source] : [];
};

const MissingBirdInfoHelp = ({ text }) => {
  const buttonRef = useRef(null);
  const popupRef = useRef(null);
  const closeTimerRef = useRef(null);
  const popupIdRef = useRef(`bird-info-help-${Math.random().toString(36).slice(2, 11)}`);
  const [isOpen, setIsOpen] = useState(false);
  const [popupStyle, setPopupStyle] = useState({ top: 0, left: 0, visibility: 'hidden' });

  const canHover = useMemo(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }

    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }, []);

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const closePopup = () => {
    clearCloseTimer();
    setIsOpen(false);
  };

  const scheduleClose = () => {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => {
      setIsOpen(false);
    }, 120);
  };

  const updatePopupPosition = () => {
    const button = buttonRef.current;
    const popup = popupRef.current;

    if (!button || !popup || typeof window === 'undefined') {
      return;
    }

    const buttonRect = button.getBoundingClientRect();
    const popupRect = popup.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const spacing = 12;

    let top = buttonRect.bottom + spacing;
    let left = buttonRect.left + (buttonRect.width / 2) - (popupRect.width / 2);

    if (left < spacing) {
      left = spacing;
    }

    if (left + popupRect.width > viewportWidth - spacing) {
      left = Math.max(spacing, viewportWidth - popupRect.width - spacing);
    }

    if (top + popupRect.height > viewportHeight - spacing) {
      top = buttonRect.top - popupRect.height - spacing;
    }

    if (top < spacing) {
      top = Math.max(spacing, viewportHeight - popupRect.height - spacing);
    }

    setPopupStyle({
      top,
      left,
      visibility: 'visible'
    });
  };

  const openPopup = () => {
    clearCloseTimer();
    setIsOpen(true);
  };

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    updatePopupPosition();

    const handlePointerDown = (event) => {
      const target = event.target;
      if (buttonRef.current?.contains(target) || popupRef.current?.contains(target)) {
        return;
      }

      closePopup();
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        closePopup();
      }
    };

    const handleViewportChange = () => {
      closePopup();
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const animationFrame = window.requestAnimationFrame(() => {
      updatePopupPosition();
    });

    return () => {
      window.cancelAnimationFrame(animationFrame);
    };
  }, [isOpen, text]);

  useEffect(() => {
    return () => {
      clearCloseTimer();
    };
  }, []);

  return (
    <span className="bird-info-help-inline">
      <span>{MISSING_BIRD_INFO_LABEL}</span>
      <button
        ref={buttonRef}
        type="button"
        className="bird-info-help-button"
        aria-label="Show help for missing bird info"
        aria-expanded={isOpen}
        aria-controls={popupIdRef.current}
        aria-describedby={isOpen ? popupIdRef.current : undefined}
        onClick={(event) => {
          event.stopPropagation();
          if (isOpen) {
            closePopup();
            return;
          }

          openPopup();
        }}
        onMouseEnter={() => {
          if (canHover) {
            openPopup();
          }
        }}
        onMouseLeave={() => {
          if (canHover) {
            scheduleClose();
          }
        }}
      >
        <span aria-hidden="true">?</span>
      </button>
      {isOpen && createPortal(
        <div
          ref={popupRef}
          id={popupIdRef.current}
          role="tooltip"
          className="bird-info-help-popup"
          style={popupStyle}
          onMouseEnter={() => {
            if (canHover) {
              clearCloseTimer();
            }
          }}
          onMouseLeave={() => {
            if (canHover) {
              scheduleClose();
            }
          }}
        >
          {text}
        </div>,
        document.body
      )}
    </span>
  );
};

const renderBirdLabelWithHelp = (birdLabel) => {
  if (birdLabel !== MISSING_BIRD_INFO_LABEL) {
    return birdLabel;
  }

  return <MissingBirdInfoHelp text={BIRD_INFO_HELP_TEXT} />;
};

const getBreederHandlerRequests = (breeder, breederRequestsById = {}, allParticipants = []) => {
  const breederId = normalizeId(breeder?.id);
  const liveRequests = breederRequestsById[breederId] || [];

  const possibleSources = [
    liveRequests,
    breeder?.handlerRequest,
    breeder?.handlerRequests,
    breeder?.breederInfo?.handlerRequest,
    breeder?.breederInfo?.handlerRequests
  ];

  allParticipants.forEach((participant) => {
    possibleSources.push(
      participant?.handlerRequest,
      participant?.handlerRequests,
      participant?.breederInfo?.handlerRequest,
      participant?.breederInfo?.handlerRequests
    );
  });

  const allRequests = possibleSources
    .flatMap(getRequestsFromSource)
    .map(normalizeRequest)
    .filter(request => normalizeId(request?.breederId) === breederId);

  const seen = new Set();

  return allRequests.filter((request, index) => {
    const key = request?.id || `${normalizeId(request?.handlerId) || 'handler'}-${request?.requestDate || 'date'}-${index}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const getHandlerDisplayName = (request, handlersById) => {
  const status = String(request?.status || '').toLowerCase();
  const canDisplayHandler = status === 'approved' || status === 'pending';

  const appendLoftName = (name, loftName) => {
    const normalizedName = toDisplayCase(name);
    const normalizedLoft = toDisplayCase(loftName);

    if (!normalizedName) return '';
    if (!normalizedLoft) return normalizedName;
    return `${normalizedName} (${normalizedLoft})`;
  };

  if (!canDisplayHandler) {
    return 'No handler selected';
  }

  if (isRotationRequest(request)) {
    return 'Rotation Bird';
  }

  const handlerFromMap = handlersById.get(normalizeId(request?.handlerId));
  const handlerIdRaw = String(firstDefined(request?.handlerId, request?.HandlerId, request?.handlerID) || '').trim();
  const handlerName = handlerFromMap?.fullName || request?.handlerName || handlerIdRaw;
  const handlerDisplay = appendLoftName(handlerName, handlerFromMap?.loftName);

  if (!handlerDisplay) {
    return 'No handler selected';
  }

  return handlerDisplay;
};

const getHandlerAssignmentsForBreeder = (breeder, allParticipants = []) => {
  const breederId = normalizeId(breeder?.id);
  if (!breederId) return [];

  return allParticipants
    .filter(participant => hasRole(participant, 'handler'))
    .flatMap((handler) => {
      const breederEntries = Array.isArray(handler?.handlerInfo?.breeders)
        ? handler.handlerInfo.breeders
        : [];

      return breederEntries
        .filter(entry => normalizeId(firstDefined(entry?.breederId, entry?.BreederId)) === breederId)
        .map((entry) => ({
          handlerId: normalizeId(handler?.id),
          handlerName: toDisplayCase(handler?.fullName || entry?.handlerName) || 'No handler selected',
          birds: Array.isArray(entry?.birds) ? entry.birds : []
        }));
    });
};

const buildBirdRowsForBreeder = (breeder, handlersById, allParticipants, breederRequestsById) => {
  const declaredTotalSlots = getBreederBirdSlotCount(breeder);
  const requests = getBreederHandlerRequests(breeder, breederRequestsById, allParticipants).filter(
    request => request?.status !== 'cancelled' && request?.status !== 'rejected'
  );

  const generatedRows = [];

  requests.forEach((request) => {
    const birds = getRequestBirds(request);
    const requestedSlotCount = Math.max(
      toNumber(request?.numberOfBirds),
      toNumber(request?.birdsRequested),
      birds.length
    );

    for (let index = 0; index < requestedSlotCount; index++) {
      const bird = birds[index] || null;

      generatedRows.push({
        id: `${breeder.id}-${request?.id || 'request'}-${index}`,
        breederId: breeder.id,
        breederName: toDisplayCase(breeder.fullName) || 'Unknown breeder',
        birdLabel: getBirdLabel(bird),
        handlerName: getHandlerDisplayName(request, handlersById),
        birdStatus: normalizeBirdStatus(bird?.status),
        requestStatus: String(request?.status || '').toLowerCase(),
      });
    }
  });

  const requestHandlerIds = new Set(
    requests
      .map(request => normalizeId(request?.handlerId))
      .filter(Boolean)
  );

  const handlerAssignments = getHandlerAssignmentsForBreeder(breeder, allParticipants);

  handlerAssignments.forEach((assignment) => {
    if (requestHandlerIds.has(assignment.handlerId)) {
      return;
    }

    const birds = assignment.birds;
    const slotCount = Math.max(1, birds.length);

    for (let index = 0; index < slotCount; index++) {
      const bird = birds[index] || null;
      generatedRows.push({
        id: `${breeder.id}-handler-assignment-${assignment.handlerId}-${index}`,
        breederId: breeder.id,
        breederName: toDisplayCase(breeder.fullName) || 'Unknown breeder',
        birdLabel: getBirdLabel(bird),
        handlerName: assignment.handlerName,
        birdStatus: normalizeBirdStatus(bird?.status),
        requestStatus: '',
      });
    }
  });

  const requiredRowsFromRequests = generatedRows.length;
  const targetSlots = Math.max(declaredTotalSlots, requiredRowsFromRequests);
  const rows = generatedRows.slice(0, targetSlots);

  while (rows.length < targetSlots) {
    rows.push({
      id: `${breeder.id}-placeholder-${rows.length}`,
      breederId: breeder.id,
      breederName: toDisplayCase(breeder.fullName) || 'Unknown breeder',
      birdLabel: MISSING_BIRD_INFO_LABEL,
      handlerName: 'No handler selected',
      birdStatus: 'active',
      requestStatus: ''
    });
  }

  const requiredRotationBirds = getRequiredRotationBirds(targetSlots);
  const existingRotationBirds = rows.filter(row => String(row.handlerName || '').toLowerCase().startsWith('rotation bird')).length;
  let missingRotationBirds = Math.max(0, requiredRotationBirds - existingRotationBirds);

  if (missingRotationBirds > 0) {
    rows.forEach((row) => {
      if (missingRotationBirds === 0) return;
      if (row.handlerName === 'No handler selected') {
        row.handlerName = 'Rotation Bird';
        missingRotationBirds -= 1;
      }
    });
  }

  return rows;
};

const BirdListPage = () => {
  const { participants, loading, error, loadParticipants } = useParticipants();
  const hasLoadedLiveRef = useRef(false);
  const [breederRequestsById, setBreederRequestsById] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBreederId, setSelectedBreederId] = useState('');
  const [selectedHandlerId, setSelectedHandlerId] = useState('');

  useEffect(() => {
    if (hasLoadedLiveRef.current) {
      return;
    }

    hasLoadedLiveRef.current = true;

    const loadData = async () => {
      try {
        await loadParticipants(true);
      } catch (loadError) {
      }
    };

    loadData();
  }, [loadParticipants]);

  const breeders = useMemo(
    () => participants.filter(participant => hasRole(participant, 'breeder')),
    [participants]
  );

  useEffect(() => {
    let isCancelled = false;

    const loadBreederRequests = async () => {
      if (breeders.length === 0) {
        if (!isCancelled) {
          setBreederRequestsById({});
        }
        return;
      }

      const requestEntries = await Promise.all(
        breeders.map(async (breeder) => {
          try {
            const result = await getHandlerRequestsByBreederId(breeder.id);
            const requests = Array.isArray(result?.requests)
              ? result.requests
              : Array.isArray(result)
              ? result
              : [];

            return [normalizeId(breeder.id), requests];
          } catch (requestError) {
            return [normalizeId(breeder.id), []];
          }
        })
      );

      if (isCancelled) return;

      const nextRequestsById = {};
      requestEntries.forEach(([breederId, requests]) => {
        nextRequestsById[breederId] = requests;
      });

      setBreederRequestsById(nextRequestsById);
    };

    loadBreederRequests();

    return () => {
      isCancelled = true;
    };
  }, [breeders]);

  const handlersById = useMemo(() => {
    const map = new Map();

    participants.forEach((participant) => {
      if (participant?.id) {
        map.set(normalizeId(participant.id), participant);
      }
    });

    return map;
  }, [participants]);

  const breederGroups = useMemo(() => {
    const breedersWithRows = breeders
      .map((breeder) => {
        const rows = buildBirdRowsForBreeder(breeder, handlersById, participants, breederRequestsById);
        return {
          breederId: breeder.id,
          breederName: toDisplayCase(breeder.fullName) || 'Unknown breeder',
          rows
        };
      })
      .filter(group => group.rows.length > 0)
      .sort((a, b) => a.breederName.localeCompare(b.breederName));

    return breedersWithRows;
  }, [breeders, participants, handlersById, breederRequestsById]);

  const birdRows = useMemo(
    () => breederGroups.flatMap(group => group.rows),
    [breederGroups]
  );

  const breederFilterOptions = useMemo(
    () => breederGroups.map(group => ({
      id: normalizeId(group.breederId),
      label: group.breederName,
    })),
    [breederGroups]
  );

  const handlerFilterOptions = useMemo(() => {
    const seen = new Map();

    birdRows.forEach((row) => {
      const handlerName = String(row.handlerName || '').trim();
      if (!handlerName) {
        return;
      }

      const key = handlerName.toLowerCase();
      if (!seen.has(key)) {
        seen.set(key, handlerName);
      }
    });

    return Array.from(seen.entries())
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [birdRows]);

  const filteredBreederGroups = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const normalizedBreederId = normalizeId(selectedBreederId);
    const normalizedHandlerId = String(selectedHandlerId || '').trim().toLowerCase();

    return breederGroups
      .filter((group) => {
        if (!normalizedBreederId) {
          return true;
        }

        return normalizeId(group.breederId) === normalizedBreederId;
      })
      .map((group) => {
        const rows = group.rows.filter((row) => {
          const matchesHandler = !normalizedHandlerId || String(row.handlerName || '').trim().toLowerCase() === normalizedHandlerId;

          if (!matchesHandler) {
            return false;
          }

          if (!normalizedSearch) {
            return true;
          }

          const searchableValues = [
            row.birdLabel,
            row.breederName,
            row.handlerName,
            row.requestStatus === 'pending' ? 'Awaiting Approval' : row.birdStatus,
          ];

          return searchableValues.some(value => String(value || '').toLowerCase().includes(normalizedSearch));
        });

        return {
          ...group,
          rows,
        };
      })
      .filter(group => group.rows.length > 0);
  }, [breederGroups, searchTerm, selectedBreederId, selectedHandlerId]);

  const filteredBirdRows = useMemo(
    () => filteredBreederGroups.flatMap(group => group.rows),
    [filteredBreederGroups]
  );

  const hasActiveFilters = Boolean(searchTerm || selectedBreederId || selectedHandlerId);

  const rotationBirdCount = useMemo(
    () => birdRows.filter(row => String(row.handlerName || '').toLowerCase().startsWith('rotation bird')).length,
    [birdRows]
  );

  if (loading) {
    return (
      <div className="container py-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading bird list...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Error</h4>
          <p>{error}</p>
          <button className="btn btn-danger" onClick={() => loadParticipants(true)}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <WelcomeBanner />
      <div className="container-fluid py-4">
        <div className="row justify-content-center">
          <div className="col-12 col-xl-11">
            <div className="text-center mb-4">
              <h1 className="display-5 mb-2" style={{ color: '#0b3d91' }}>
                Bird List
              </h1>
              <p className="text-muted mb-0">
                Combined breeder and handler request view for all breeder bird entries
              </p>
            </div>

            <div className="row g-3 mb-4">
              <div className="col-12 col-md-4">
                <div className="card shadow-sm h-100">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Breeders</h6>
                    <h3 className="mb-0" style={{ color: '#0b3d91' }}>{breederGroups.length}</h3>
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-4">
                <div className="card shadow-sm h-100">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Total Bird Entries</h6>
                    <h3 className="mb-0" style={{ color: '#0b3d91' }}>{birdRows.length}</h3>
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-4">
                <div className="card shadow-sm h-100">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Rotation Birds</h6>
                    <h3 className="mb-0" style={{ color: '#0b3d91' }}>{rotationBirdCount}</h3>
                  </div>
                </div>
              </div>
            </div>

            {birdRows.length === 0 ? (
              <div className="alert alert-info text-center">
                <p className="mb-0">No bird entries are available yet.</p>
              </div>
            ) : (
              <>
                <div className="card shadow-sm mb-4 bird-list-filters">
                  <div className="card-body">
                    <div className="row g-3 align-items-end">
                      <div className="col-12 col-md-6 col-xl-4">
                        <label htmlFor="birdSearchFilter" className="form-label fw-bold">Search birds</label>
                        <input
                          id="birdSearchFilter"
                          type="text"
                          className="form-control bird-list-filter-control"
                          placeholder="Search by bird, breeder, handler, or status"
                          value={searchTerm}
                          onChange={(event) => setSearchTerm(event.target.value)}
                        />
                      </div>
                      <div className="col-12 col-md-6 col-xl-3">
                        <label htmlFor="birdBreederFilter" className="form-label fw-bold">Filter by breeder</label>
                        <select
                          id="birdBreederFilter"
                          className="form-select bird-list-filter-control"
                          value={selectedBreederId}
                          onChange={(event) => setSelectedBreederId(event.target.value)}
                        >
                          <option value="">All breeders</option>
                          {breederFilterOptions.map(option => (
                            <option key={option.id} value={option.id}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-12 col-md-6 col-xl-3">
                        <label htmlFor="birdHandlerFilter" className="form-label fw-bold">Filter by handler</label>
                        <select
                          id="birdHandlerFilter"
                          className="form-select bird-list-filter-control"
                          value={selectedHandlerId}
                          onChange={(event) => setSelectedHandlerId(event.target.value)}
                        >
                          <option value="">All handlers</option>
                          {handlerFilterOptions.map(option => (
                            <option key={option.id} value={option.id}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-12 col-md-6 col-xl-2">
                        <div className="bird-list-filter-actions">
                          <button
                            type="button"
                            className="btn btn-outline-secondary w-100 bird-list-filter-control"
                            onClick={() => {
                              setSearchTerm('');
                              setSelectedBreederId('');
                              setSelectedHandlerId('');
                            }}
                            disabled={!hasActiveFilters}
                          >
                            Clear Filters
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bird-list-results-summary mb-3">
                  <h5 className="mb-0">
                    Showing {filteredBirdRows.length} of {birdRows.length} birds
                  </h5>
                  {hasActiveFilters && (
                    <span className="text-muted small">Filters are applied to the current bird list.</span>
                  )}
                </div>

                {filteredBirdRows.length === 0 ? (
                  <div className="alert alert-info text-center">
                    <p className="mb-0">No birds found matching your filters.</p>
                  </div>
                ) : (
                  <>
                <div className="card shadow-sm participant-table-desktop">
                  <div className="card-body p-0">
                    <div className="table-responsive">
                      <table className="table table-striped mb-0">
                        <thead className="table-dark">
                          <tr>
                            <th className="px-3 py-3 fw-bold">Bird / Band Number</th>
                            <th className="px-3 py-3 fw-bold">Breeder</th>
                            <th className="px-3 py-3 fw-bold">Handler</th>
                            <th className="px-3 py-3 fw-bold">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredBreederGroups.map(group => (
                            <React.Fragment key={group.breederId}>
                              <tr className="table-primary">
                                <td className="px-3 py-2 fw-bold" colSpan={4}>
                                  {group.breederName} ({group.rows.length} {group.rows.length === 1 ? 'bird' : 'birds'})
                                </td>
                              </tr>
                              {group.rows.map(row => (
                                <tr key={row.id}>
                                  <td className="px-3 py-3">{renderBirdLabelWithHelp(row.birdLabel)}</td>
                                  <td className="px-3 py-3">{row.breederName}</td>
                                  <td className={`px-3 py-3 ${row.requestStatus === 'pending' ? 'text-warning fw-semibold' : ''}`}>{row.handlerName}</td>
                                  <td className="px-3 py-3">
                                    <span className={`badge ${
                                      row.requestStatus === 'pending' ? 'bg-warning text-dark' :
                                      row.birdStatus === 'active' ? 'bg-success' :
                                      row.birdStatus === 'injured' ? 'bg-warning text-dark' :
                                      'bg-danger'
                                    }`}>
                                      {row.requestStatus === 'pending' ? 'Awaiting Approval' : row.birdStatus}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <div className="participant-card-mobile">
                  {filteredBreederGroups.map(group => (
                    <div key={group.breederId} className="participant-card">
                      <div className="participant-card-header">
                        <div>
                          <h6 className="participant-name mb-0">{group.breederName}</h6>
                          <small className="text-muted">{group.rows.length} {group.rows.length === 1 ? 'bird' : 'birds'}</small>
                        </div>
                      </div>

                      <div className="participant-info">
                        {group.rows.map(row => (
                          <div key={row.id} className="border rounded p-2 mb-2">
                            <div className="info-row">
                              <span className="info-label">Bird:</span>
                              <span className="info-value">{renderBirdLabelWithHelp(row.birdLabel)}</span>
                            </div>
                            <div className="info-row">
                              <span className="info-label">Handler:</span>
                              <span className={`info-value ${row.requestStatus === 'pending' ? 'text-warning fw-semibold' : ''}`}>{row.handlerName}</span>
                            </div>
                            <div className="info-row">
                              <span className="info-label">Status:</span>
                              <span className="info-value">{row.requestStatus === 'pending' ? 'Awaiting Approval' : row.birdStatus}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                  </>
                )}
              </>
            )}

            <BackToDashboardButton />
          </div>
        </div>
      </div>
    </>
  );
};

export default BirdListPage;